import React, { useRef, useEffect, useCallback, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useKeyboard, isDesktopPlatform } from './KeyboardContext';
import NumericKeyboard from './NumericKeyboard';
import QwertyKeyboard from './QwertyKeyboard';
import WinnersPadKeyboard from './WinnersPadKeyboard';
import { pushBackHandler, popBackHandler } from '../../utils/backHandler';

const getFaviconUrl = (url) => {
    if (!url) return null;
    try {
        const normalized = /^https?:\/\//i.test(url) ? url : `https://${url}`;
        const domain = new URL(normalized).hostname;
        return `https://www.google.com/s2/favicons?domain=${domain}&sz=64`;
    } catch {
        return null;
    }
};

const formatRecentLinkLabel = (link) => {
    const raw = String(link || '').trim();
    if (!raw) return '';
    try {
        const normalized = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
        const parsed = new URL(normalized);
        if (/(^|\.)t\.me$/i.test(parsed.hostname) || /(^|\.)telegram\.(me|org)$/i.test(parsed.hostname)) {
            const username = parsed.pathname.replace(/^\//, '').split('/')[0];
            if (username) return `@${username}`;
        }
    } catch {
        // keep raw
    }
    if (raw.startsWith('@')) return raw;
    return raw;
};

/** Max 10 chars; if longer — first 9 + "..." */
const truncateRecentLinkLabel = (link) => {
    const label = formatRecentLinkLabel(link);
    if (label.length <= 10) return label;
    return `${label.slice(0, 9)}...`;
};

const RecentLinkIcon = ({ link }) => {
    const faviconUrl = getFaviconUrl(link);
    const [failed, setFailed] = useState(false);

    if (!faviconUrl || failed) {
        return (
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71" stroke="#2B8FF4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71" stroke="#2B8FF4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
        );
    }

    return (
        <img
            src={faviconUrl}
            alt=""
            className="w-full h-full object-cover"
            onError={() => setFailed(true)}
        />
    );
};

const CustomKeyboard = () => {
    const { 
        isOpen, 
        keyboardType, 
        inputValue, 
        cursorPosition,
        inputLabel, 
        clipboardContent,
        recentLinks,
        recentLinksLabel,
        pasteFromClipboard,
        insertText,
        applyInputValue,
        setInputValue,
        setCursorPosition,
        closeKeyboard 
    } = useKeyboard();

    const pasteInputRef = useRef(null);
    const [showAllRecent, setShowAllRecent] = useState(false);
    const isPromoterSuggestions = recentLinksLabel === '';
    const visibleRecentLinks = isPromoterSuggestions && !showAllRecent
        ? recentLinks.slice(0, 3)
        : recentLinks;

    useEffect(() => {
        if (!isOpen) setShowAllRecent(false);
    }, [isOpen]);

    // Перехват кнопки "назад" Telegram — закрыть клавиатуру без сохранения
    useEffect(() => {
        if (!isOpen) return;
        const dismissKeyboard = () => closeKeyboard({ applied: false });
        pushBackHandler(dismissKeyboard);
        return () => popBackHandler(dismissKeyboard);
    }, [isOpen, closeKeyboard]);

    // Синхронизируем реальный input с состоянием клавиатуры
    useEffect(() => {
        const el = pasteInputRef.current;
        if (!el || !isOpen) return;
        if (el.value !== inputValue) {
            el.value = inputValue;
        }
        // Ставим курсор
        try {
            el.setSelectionRange(cursorPosition, cursorPosition);
        } catch (e) { /* ignore */ }
    }, [inputValue, cursorPosition, isOpen]);

    // Фокусируемся на paste-input при открытии
    useEffect(() => {
        if (isOpen && pasteInputRef.current) {
            // Небольшая задержка чтобы анимация начала проигрываться
            setTimeout(() => {
                pasteInputRef.current?.focus();
            }, 100);
        }
    }, [isOpen]);

    // Обработка paste на реальном input
    const handlePaste = useCallback((e) => {
        e.preventDefault();
        const text = (e.clipboardData || window.clipboardData)?.getData('text');
        if (text && text.trim()) {
            insertText(text.trim());
        }
    }, [insertText]);

    const handleBeforeInput = useCallback((e) => {
        if (isDesktopPlatform) return;
        e.preventDefault();
    }, []);

    const handleKeyDown = useCallback((e) => {
        if (isDesktopPlatform) {
            if (e.key === 'Enter') {
                e.preventDefault();
                closeKeyboard();
            }
            return;
        }
        if ((e.ctrlKey || e.metaKey) && e.key === 'v') return;
        e.preventDefault();
    }, [closeKeyboard]);

    return (
        <AnimatePresence>
            {isOpen && (
                <>
                    {/* Invisible tap catcher — no dimming */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[998]"
                        onClick={() => closeKeyboard({ applied: false })}
                    />
                    
                    {/* Keyboard container */}
                    <motion.div
                        initial={{ y: '100%' }}
                        animate={{ y: 0 }}
                        exit={{ y: '100%' }}
                        transition={{ type: 'spring', damping: 30, stiffness: 300 }}
                        data-custom-keyboard="1"
                        className="fixed bottom-0 left-0 right-0 z-[999]"
                    >
                        {/* Border top */}
                        <div 
                            className="h-[1px] w-full"
                            style={{ background: '#2C2C2C' }}
                        />
  
                        {/* Main keyboard body */}
                        <div className="bg-[#1A1A1C]">
                            {/* Header with label */}
                            {inputLabel && (
                                <div className="w-full px-4 pt-3 pb-1 flex items-center gap-2">
                                    <div className="w-[6px] h-[6px] rounded-full bg-[#2B8FF4] flex-shrink-0" />
                                    <span className="text-[#2B8FF4] text-[13px] font-medium flex-1">
                                        {inputLabel}
                                    </span>
                                    {isPromoterSuggestions && recentLinks.length > 3 && (
                                        <button
                                            type="button"
                                            onClick={() => setShowAllRecent((v) => !v)}
                                            className="flex items-center gap-1.5 flex-shrink-0"
                                        >
                                            <span className="text-white text-[12px]">
                                                {showAllRecent ? 'see less' : 'see more ...'}
                                            </span>
                                            <span className="min-w-[18px] h-[18px] px-1 rounded-full bg-[#FB2C36] text-white text-[10px] font-semibold flex items-center justify-center">
                                                {recentLinks.length}
                                            </span>
                                        </button>
                                    )}
                                </div>
                            )}

                            {recentLinks.length > 0 && (
                                <div className="w-full px-4 pt-2 pb-1">
                                    {!isPromoterSuggestions && recentLinksLabel ? (
                                        <div className="text-[11px] text-[#959595] font-medium mb-2 px-1">
                                            {recentLinksLabel}
                                        </div>
                                    ) : null}
                                    {isPromoterSuggestions ? (
                                        <div
                                            className="grid grid-cols-3 gap-2 overscroll-contain"
                                            style={{ WebkitOverflowScrolling: 'touch' }}
                                        >
                                            {visibleRecentLinks.map((link) => (
                                                <button
                                                    key={link}
                                                    type="button"
                                                    onClick={() => applyInputValue(link)}
                                                    className="min-w-0 h-[25px] pl-0.5 pr-2 rounded-full flex items-center gap-1
                                                        active:scale-[0.98] transition-all duration-100"
                                                    style={{
                                                        background: 'rgba(234, 234, 234, 0.04)',
                                                        border: '1px solid rgba(234, 234, 234, 0.85)',
                                                    }}
                                                >
                                                    <div className="w-[20px] h-[20px] rounded-full bg-white flex-shrink-0" />
                                                    <span className="text-white text-[12px] truncate min-w-0">
                                                        {truncateRecentLinkLabel(link)}
                                                    </span>
                                                </button>
                                            ))}
                                        </div>
                                    ) : (
                                        <div
                                            className={`${
                                                recentLinksLabel
                                                    ? 'grid grid-cols-2 gap-2'
                                                    : 'flex flex-col gap-2'
                                            } max-h-[132px] overflow-y-auto overscroll-contain`}
                                            style={{ WebkitOverflowScrolling: 'touch' }}
                                        >
                                            {recentLinks.map((link) => (
                                                <button
                                                    key={link}
                                                    type="button"
                                                    onClick={() => applyInputValue(link)}
                                                    className={`w-full flex items-center gap-2 rounded-[10px]
                                                        active:scale-[0.98] transition-all duration-100 ${
                                                            recentLinksLabel ? 'px-2 py-1' : 'px-3 py-2'
                                                        }`}
                                                    style={{
                                                        background: 'rgba(234, 234, 234, 0.04)',
                                                        border: '1px solid rgba(234, 234, 234, 0.08)',
                                                    }}
                                                >
                                                    <div className="w-[16px] h-[16px] rounded-[8px] overflow-hidden flex items-center justify-center flex-shrink-0"
                                                        style={{ background: 'rgba(43, 143, 244, 0.15)' }}
                                                    >
                                                        <RecentLinkIcon link={link} />
                                                    </div>
                                                    <div className="flex-1 text-left overflow-hidden">
                                                        <div className="text-white text-[11px] truncate">{formatRecentLinkLabel(link)}</div>
                                                    </div>
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}
                            
                            {/* Clipboard suggestion */}
                            {clipboardContent && (
                                <div className="w-full px-4 py-2">
                                    <button
                                        onClick={pasteFromClipboard}
                                        className="w-full flex items-center gap-3 px-3 py-2.5 rounded-[10px]
                                            active:scale-[0.98] transition-all duration-100"
                                        style={{
                                            background: 'rgba(43, 143, 244, 0.08)',
                                            border: '1px solid rgba(43, 143, 244, 0.25)',
                                        }}
                                    >
                                        <div className="w-[32px] h-[32px] rounded-[8px] flex items-center justify-center flex-shrink-0"
                                            style={{
                                                background: '#2B8FF4',
                                            }}
                                        >
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                                <path 
                                                    d="M16 4H18C19.1046 4 20 4.89543 20 6V20C20 21.1046 19.1046 22 18 22H6C4.89543 22 4 21.1046 4 20V6C4 4.89543 4.89543 4 6 4H8" 
                                                    stroke="#1A1A1C" strokeWidth="2" strokeLinecap="round"
                                                />
                                                <rect x="8" y="2" width="8" height="4" rx="1" stroke="#1A1A1C" strokeWidth="2"/>
                                            </svg>
                                        </div>
                                        <div className="flex-1 text-left overflow-hidden">
                                            <div className="text-[11px] text-[#2B8FF4] font-medium mb-0.5">
                                                Paste from clipboard
                                            </div>
                                            <div className="text-white text-[14px] truncate">
                                                {clipboardContent}
                                            </div>
                                        </div>
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className="flex-shrink-0">
                                            <path 
                                                d="M5 12H19M19 12L12 5M19 12L12 19" 
                                                stroke="#EAEAEA" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                                            />
                                        </svg>
                                    </button>
                                </div>
                            )}
                            
                            {/* 
                              Input preview — реальный <input> с inputMode="none"
                              inputMode="none" блокирует виртуальную клавиатуру,
                              но разрешает нативное контекстное меню (long-press → Paste)
                            */}
                            <div className={`w-full px-4 ${inputLabel || clipboardContent || recentLinks.length > 0 ? 'py-2' : 'py-3'}`}>
                                <div 
                                    className="w-full min-h-[40px] px-4 py-2 rounded-[12px] flex items-center"
                                    style={{
                                        background: 'rgba(43, 143, 244, 0.06)',
                                        border: '1px solid rgba(43, 143, 244, 0.2)',
                                    }}
                                >
                                    <input
                                        ref={pasteInputRef}
                                        data-kb-paste-input="1"
                                        inputMode={isDesktopPlatform ? undefined : "none"}
                                        autoComplete="off"
                                        autoCorrect="off"
                                        autoCapitalize="off"
                                        spellCheck={false}
                                        defaultValue={inputValue}
                                        onPaste={handlePaste}
                                        onBeforeInput={handleBeforeInput}
                                        onKeyDown={handleKeyDown}
                                        onChange={isDesktopPlatform ? (e) => {
                                            setInputValue(e.target.value);
                                            setCursorPosition(e.target.selectionStart || e.target.value.length);
                                        } : undefined}
                                        className="w-full bg-transparent text-white text-[16px] outline-none border-none caret-[#2B8FF4] placeholder-[#6C6C70]"
                                        placeholder={isDesktopPlatform ? "Type here..." : "Long press to paste..."}
                                        style={{ caretColor: '#2B8FF4' }}
                                    />
                                </div>
                            </div>
                            
                            {!isDesktopPlatform && (
                                keyboardType === 'numeric' ? (
                                    <NumericKeyboard />
                                ) : keyboardType === 'winners-pad' ? (
                                    <WinnersPadKeyboard />
                                ) : (
                                    <QwertyKeyboard />
                                )
                            )}

                            {isDesktopPlatform && (
                                <div className="flex flex-row gap-2 px-4 pb-14">
                                    <button
                                        type="button"
                                        onClick={closeKeyboard}
                                        className="flex-1 h-[44px] rounded-[10px] flex items-center justify-center
                                            active:scale-[0.98] transition-all duration-100"
                                        style={{ background: '#2B8FF4' }}
                                    >
                                        <span className="text-white text-[15px] font-semibold">Apply</span>
                                    </button>
                                </div>
                            )}
                            
                            {/* Safe area spacer */}
                            <div className="h-[env(safe-area-inset-bottom)]" />
                        </div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
};

export default CustomKeyboard;
